import * as THREE from 'three';
import { terrainHeight } from '../sim/terrain';
import { PARAMS } from '../sim/params';

/**
 * Screen -> world picking. The ground is an analytic heightfield, so instead
 * of raycasting terrain triangles we solve ray.y == terrainHeight iteratively
 * (2 refinement steps is plenty at our camera pitches).
 */
export class GroundPicker {
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly p = new THREE.Vector3();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly canvas: HTMLCanvasElement,
  ) {}

  private setRay(clientX: number, clientY: number): void {
    const r = this.canvas.getBoundingClientRect();
    this.ndc.set(
      ((clientX - r.left) / r.width) * 2 - 1,
      -((clientY - r.top) / r.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.ndc, this.camera);
  }

  /** Ground point under the screen position, clamped to the island. Null if sky. */
  pickGround(clientX: number, clientY: number): { x: number; z: number } | null {
    this.setRay(clientX, clientY);
    const o = this.raycaster.ray.origin;
    const d = this.raycaster.ray.direction;
    if (d.y > -1e-4) return null; // looking up
    let t = -o.y / d.y; // y = 0 first guess
    for (let iter = 0; iter < 2; iter++) {
      this.p.copy(o).addScaledVector(d, t);
      const h = terrainHeight(this.p.x, this.p.z);
      t = (h - o.y) / d.y;
      if (t < 0) return null;
    }
    this.p.copy(o).addScaledVector(d, t);
    let { x, z } = this.p;
    const r = Math.hypot(x, z);
    const limit = PARAMS.ISLAND_RADIUS * 0.95;
    if (r > limit) {
      x *= limit / r;
      z *= limit / r;
    }
    return { x, z };
  }

  /** Instance id of the creature under the screen position, or null. */
  pickCreature(clientX: number, clientY: number, mesh: THREE.InstancedMesh): number | null {
    this.setRay(clientX, clientY);
    const hits = this.raycaster.intersectObject(mesh, false);
    const hit = hits[0];
    return hit && hit.instanceId !== undefined ? hit.instanceId : null;
  }
}
